import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { apiEndpoints } from '../api-endpoints';
import { HttpClient } from '@angular/common/http';
import { finalize, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { 
  GetUserAppListResponse, 
  UserAppResponse, 
  ApplicationIdDto, 
  AppUserType,
  CreateAppUserRequest,
  DeleteAppUserByIdRequest,
  DeleteAppUserByEmailRequest,
  UpdateAppUserRoleRequest 
} from './models/app-user.model';
import { AppState } from './models/application-details.model';
import { select, Store } from '@ngrx/store';
import { selectAppList } from '../store/app-state.selectors';
import { setAppList } from '../store/app-state.actions';

@Injectable({
  providedIn: 'root'
})
export class AppUserService {

    private apiUrl = environment.apiConfig.baseUri + apiEndpoints.appUsers;

    private apis = {
      getAppsForUser: '/get-apps-for-user',
      getUsersForApp: '/get-users-for-app',
      createUser: '/create',
      deleteUserById: '/delete-by-userid',
      deleteUserByEmail: '/delete-by-email',
      updateUserRole: '/update-role'
    }

    /** In-flight app-list request, shared so a page load fetches at most once. */
    private pendingAppList$: Observable<UserAppResponse[]> | null = null;

    constructor(
      private http: HttpClient,
      private store: Store<AppState>
    )
    {
    }

    getUserAppList(): Observable<UserAppResponse[]> {
      return this.store.pipe(
        select(selectAppList),
        take(1),
        switchMap(appList => {
          //Case where we already found the map list in the store
          if (appList && appList.length > 0) {
            return of(appList);
          }

          // The top bar and the /loading page both ask for this on a cold load,
          // so share one request between them instead of firing two. Cleared on
          // completion, so a failed lookup can be retried.
          if (!this.pendingAppList$) {
            this.pendingAppList$ = this.http.get<GetUserAppListResponse>(`${this.apiUrl}${this.apis.getAppsForUser}`, {headers: { 'skipAppId': 'true' }}).pipe(
              // Map an empty/null body to [] rather than filtering it out. Filtering
              // completes the stream with no value, which silently stalls any caller
              // that waits on a single emission.
              map(response => response?.appList ?? []),
              tap(list => {
                //Dispatch an action to store the app list in the state
                this.store.dispatch(setAppList({appList: list}));
              }),
              finalize(() => { this.pendingAppList$ = null; }),
              shareReplay({ bufferSize: 1, refCount: false })
            );
          }

          return this.pendingAppList$;
        })
      );
    }

    getUsersForApp(applicationId: number): Observable<UserAppResponse[]> {
      const request: ApplicationIdDto = { applicationId };
      return this.http.post<GetUserAppListResponse>(`${this.apiUrl}${this.apis.getUsersForApp}`, request).pipe(
        map(response => response.appList || [])
      );
    }

    createUser(request: CreateAppUserRequest): Observable<any> {
      return this.http.post(`${this.apiUrl}${this.apis.createUser}`, request);
    }

    deleteUserById(userId: number): Observable<any> {
      const request: DeleteAppUserByIdRequest = { userId };
      return this.http.post(`${this.apiUrl}${this.apis.deleteUserById}`, request);
    }

    deleteUserByEmail(applicationId: number, email: string): Observable<any> {
      const request: DeleteAppUserByEmailRequest = { applicationId, email };
      return this.http.post(`${this.apiUrl}${this.apis.deleteUserByEmail}`, request);
    }

    updateUserRole(applicationId: number, email: string, newRole: AppUserType): Observable<any> {
      const request: UpdateAppUserRoleRequest = { applicationId, email, newRole };
      return this.http.post(`${this.apiUrl}${this.apis.updateUserRole}`, request);
    }
}
